import { useState } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Textarea } from '@/components/ui'
import { Type, Copy, Check, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

type CaseType =
  | 'lowercase'
  | 'uppercase'
  | 'titlecase'
  | 'sentencecase'
  | 'camelcase'
  | 'pascalcase'
  | 'snakecase'
  | 'kebabcase'
  | 'dotcase'
  | 'pathcase'
  | 'constantcase'
  | 'alternating'
  | 'inverse'

interface CaseOption {
  id: CaseType
  label: string
  example: string
}

const caseOptions: CaseOption[] = [
  { id: 'lowercase', label: 'lowercase', example: 'hello world' },
  { id: 'uppercase', label: 'UPPERCASE', example: 'HELLO WORLD' },
  { id: 'titlecase', label: 'Title Case', example: 'Hello World' },
  { id: 'sentencecase', label: 'Sentence case', example: 'Hello world' },
  { id: 'camelcase', label: 'camelCase', example: 'helloWorld' },
  { id: 'pascalcase', label: 'PascalCase', example: 'HelloWorld' },
  { id: 'snakecase', label: 'snake_case', example: 'hello_world' },
  { id: 'kebabcase', label: 'kebab-case', example: 'hello-world' },
  { id: 'dotcase', label: 'dot.case', example: 'hello.world' },
  { id: 'pathcase', label: 'path/case', example: 'hello/world' },
  { id: 'constantcase', label: 'CONSTANT_CASE', example: 'HELLO_WORLD' },
  { id: 'alternating', label: 'aLtErNaTiNg', example: 'hElLo WoRlD' },
  { id: 'inverse', label: 'Inverse', example: 'Swaps case' },
]

export function TextCase() {
  const [input, setInput] = useState('')
  const [output, setOutput] = useState('')
  const [copied, setCopied] = useState(false)

  const toWords = (text: string): string[] => {
    return text
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[_\-./\\]/g, ' ')
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean)
  }

  const convertCase = (text: string, caseType: CaseType): string => {
    const words = toWords(text)

    switch (caseType) {
      case 'lowercase':
        return text.toLowerCase()

      case 'uppercase':
        return text.toUpperCase()

      case 'titlecase':
        return text
          .toLowerCase()
          .split(' ')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')

      case 'sentencecase':
        return text
          .toLowerCase()
          .replace(/(^\s*\w|[.!?]\s*\w)/g, (c) => c.toUpperCase())

      case 'camelcase':
        return words
          .map((word, i) => (i === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
          .join('')

      case 'pascalcase':
        return words
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join('')

      case 'snakecase':
        return words.join('_')

      case 'kebabcase':
        return words.join('-')

      case 'dotcase':
        return words.join('.')

      case 'pathcase':
        return words.join('/')

      case 'constantcase':
        return words.join('_').toUpperCase()

      case 'alternating':
        return text
          .split('')
          .map((char, i) => (i % 2 === 0 ? char.toLowerCase() : char.toUpperCase()))
          .join('')

      case 'inverse':
        return text
          .split('')
          .map((char) =>
            char === char.toUpperCase() ? char.toLowerCase() : char.toUpperCase()
          )
          .join('')

      default:
        return text
    }
  }

  const handleConvert = (caseType: CaseType) => {
    if (!input.trim()) {
      toast.error('Please enter some text')
      return
    }
    const converted = convertCase(input, caseType)
    setOutput(converted)
    toast.success(`Converted to ${caseType}`)
  }

  const copyOutput = async () => {
    if (!output) return

    try {
      await navigator.clipboard.writeText(output)
      setCopied(true)
      toast.success('Copied to clipboard')
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Failed to copy')
    }
  }

  const clearAll = () => {
    setInput('')
    setOutput('')
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <Type className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Text Case Converter</h1>
            <p className="text-muted-foreground">Convert text between different case formats</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Input */}
        <Card className="glass">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Input</CardTitle>
                <CardDescription>Enter text to convert</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={clearAll}>
                <Trash2 className="h-4 w-4 mr-1" />
                Clear
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Enter your text here..."
              className="min-h-[200px]"
            />
          </CardContent>
        </Card>

        {/* Output */}
        <Card className="glass">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Output</CardTitle>
                <CardDescription>Converted text</CardDescription>
              </div>
              {output && (
                <Button variant="outline" size="sm" onClick={copyOutput}>
                  {copied ? (
                    <Check className="h-4 w-4 mr-1 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4 mr-1" />
                  )}
                  Copy
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <Textarea
              value={output}
              readOnly
              placeholder="Converted text will appear here..."
              className="min-h-[200px]"
            />
          </CardContent>
        </Card>
      </div>

      {/* Case Options */}
      <Card className="glass">
        <CardHeader>
          <CardTitle>Case Options</CardTitle>
          <CardDescription>Click a button to convert your text</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {caseOptions.map((option) => (
              <Button
                key={option.id}
                variant="outline"
                className="h-auto py-3 flex-col gap-1"
                onClick={() => handleConvert(option.id)}
              >
                <span className="font-medium">{option.label}</span>
                <span className="text-xs text-muted-foreground">{option.example}</span>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
